import React, { useEffect } from "react";
import FeatherIcon from "feather-icons-react";
import moment from "moment-timezone";
import styles from "./ViewAppointmentModal.module.scss";
import { DropdownBase } from "../../atoms";
import openNotification from "../../../utilities/openNotification";
import { useQueryClient } from "react-query";
import { assign, compact, filter, get, includes, isEmpty, map, times } from "lodash";
import { Avatar, Badge, Menu, Select, Table } from "antd";
import EllipsisLabel from "../../atoms/ellipsisLabel/EllipsisLabel";
import shiftApi from "../../../utilities/api/shift";
import { CheckCircle } from "react-feather";
import { Link } from "react-router-dom";
import Restricted from "../../../PermissionsProvider/Restricted";
import BreakModalEdit from "./BreakModalEdit";
import { Button } from "../../atoms/buttons/buttons";

const getDsEditPopupContainer = (styles) => (triggerNode) =>
    triggerNode?.closest(`.${styles["ds-edit-root"]}`) || document.body;

function buildCancelSlotMenuItem({ slotId, cartProductId, b2bType, onCancel }) {
    return {
        label: (
            <Restricted
                both={true}
                to="remove_slot"
                resource={b2bType === "employee_wellness" ? "employee_wellness" : "shift"}
                fallback={<></>}
            >
                <Link
                    to="#"
                    onClick={(e) => {
                        e?.preventDefault();
                        onCancel(slotId, cartProductId);
                    }}
                >
                    <FeatherIcon icon="minus-circle" size={18} />
                    Cancel Slot
                </Link>
            </Restricted>
        ),
        key: "cancel-slot",
    };
}

function SlotCancelDropdown({
    slotId,
    cartProductId,
    b2bType,
    onCancel,
    styles,
    iconColor = "#ADB4D2",
    iconSize = 18,
    className = "actions-dropdown",
}) {
    return (
        <DropdownBase
            style={{ margin: "auto", height: "20px" }}
            className={className}
            classNameC="actions-dropdown-content-pt-0"
            action={["click"]}
            getPopupContainer={getDsEditPopupContainer(styles)}
            content={
                <Menu
                    items={[buildCancelSlotMenuItem({ slotId, cartProductId, b2bType, onCancel })]}
                />
            }
        >
            <span
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                    }
                }}
                style={{ display: "inline-flex", lineHeight: 0 }}
            >
                <FeatherIcon color={iconColor} icon="more-vertical" size={iconSize} />
            </span>
        </DropdownBase>
    );
}

function AppointmentDSEdit(props) {
    const queryClient = useQueryClient();
    const { appointment, isLoading, setIsLoading, refetch, refetchWaivableFees, forceRefetch
    } = props;
    const {
        offers_json,
        cart_products,
        appointment_number,
        business_id,
        location_id,
        time,
        cart_end_time,
        session_date,
        b2b_type,
    } = appointment || {};
    const slot_length_minutes = get(cart_products, "0.slots.0.length", 0);
    const break_minutes = get(cart_products, "0.break_minutes", 0);
    const start_time = moment(time?.utc).tz(time?.timezone);
    const end_time = moment(cart_end_time.utc).tz(time?.timezone);
    const shiftLength = end_time.diff(start_time, "minutes");
    const number_of_slots = shiftLength / slot_length_minutes;
    const cellToAdd = break_minutes ? 1 : 0;
    
    // Base scale for consistent time-to-pixel conversion across all elements
    const BASE_SCALE_PX_PER_MINUTE = 3;
    
    // Dynamic height calculation for general UI elements (with constraints)
    const getProportionalHeight = (minutes) => {
        // Minimum height for readability
        const minHeight = 35;
        // Maximum height to prevent excessive sizes
        const maxHeight = 200;
        
        const calculatedHeight = minutes * BASE_SCALE_PX_PER_MINUTE;
        return Math.max(minHeight, Math.min(maxHeight, calculatedHeight));
    };
    
    // Precise height calculation for time-aligned elements (no constraints)
    const getExactTimeHeight = (minutes) => {
        return minutes * BASE_SCALE_PX_PER_MINUTE;
    };
    
    // Precise offset calculation for time-aligned positioning
    const getExactTimeOffset = (offsetMinutes) => {
        return offsetMinutes > 0 ? (offsetMinutes * BASE_SCALE_PX_PER_MINUTE) : 0;
    };
    
    // Calculate base time slot height proportional to slot length
    const baseTimeSlotHeight = getProportionalHeight(slot_length_minutes);

    const [setOfTimes, setSetOfTimes] = React.useState([]);
    const [showEditBreakModal, setShowEditBreakModal] = React.useState(false);
    const [breakObj, setBreakObj] = React.useState({});
    const [slotOptions, setSlotOptions] = React.useState([]);
    const [isSchedulerExpanded, setIsSchedulerExpanded] = React.useState(false);

    const providerCount = cart_products?.length || 0;
    const providerColumnMinWidth = providerCount >= 4 ? 150 : providerCount >= 3 ? 168 : 132;
    const tableScrollX = 120 + providerCount * providerColumnMinWidth;

    useEffect(() => {
        initializeTimes();
    }, [appointment]);

    const assignProvider = (pro_id, cart_product_id) => {
        setIsLoading(true);
        let data = {
            business_id: business_id,
            location_id: location_id,
            cart_id: appointment_number,
            pro_id,
            action: "assign_provider",
            cart_product_id
        };
        shiftApi["assignProviderBySlot"](data)
            .then((response) => {
                openNotification("Success", response.data.data.message, "success");
                setIsLoading(false);
                refetch();
                refetchWaivableFees();
                queryClient.refetchQueries("appointments");
                queryClient.refetchQueries("shifts");
                queryClient.refetchQueries("allBusinessesAppointments");
                queryClient.refetchQueries("businessAppointments");
            })
            .catch((error) => {
                setIsLoading(false);
                if (error.response.status === 401 || error.response.status === 500) {
                    openNotification("Error", error.response.data.message, "error");
                } else
                    openNotification(
                        "Error",
                        error.response.data?.data[0].message,
                        "error"
                    );
            });
    };

    const cancelSlot = (slot_id, cart_product_id) => {
        setIsLoading(true);
        shiftApi["cancelSlot"]({
            slot_id,
            location_id: location_id,
        })
            .then((response) => {
                openNotification("Success", response.data.message, "success");
                setIsLoading(false);
                refetch();
                refetchWaivableFees();
            })
            .catch((error) => {
                setIsLoading(false);
                openNotification("Error", error.response?.data?.message, "error");
            })
    }

    const initializeTimes = () => {
        let tempObj = [];
        times(number_of_slots, (i) => {
            let newTemp = moment(start_time).clone().add(i * slot_length_minutes, "minutes");
            tempObj.push({
                displayTime: newTemp.format("h:mm A"),
                start_time: newTemp,
            });
        })
        setSetOfTimes(tempObj);
    }

    const getNameOfPro = (pro, hideBadge = false) => {
        const { name, status, image } = pro;
        return <div className={`${styles["flex-row"]} ${styles["pro-list"]}`}>
            {hideBadge ?
                <Avatar src={image} size={28} />
                : <Badge
                    offset={[-5, 24]}
                    style={{
                        backgroundColor:
                            status === "Offer Expired"
                                ? "#AFAFAF"
                                : status === "pending"
                                    ? "#FFC043"
                                    : status === "rejected"
                                        ? "#AFAFAF"
                                        : status === "cancelled"
                                            ? "#FF4D4F"
                                            : status === "error"
                                                ? "#FF4D4F"
                                                : "#20C997",
                        borderRadius: "50%",
                    }}
                    count={
                        status === "pending" ? (
                            <FeatherIcon
                                icon="more-horizontal"
                                color="#fff"
                                size={14}
                            />
                        ) : status === "Offer Expired" ||
                            status === "error" ||
                            status === "rejected" ||
                            status === "cancelled" ? (
                            <FeatherIcon icon="x" color="#fff" size={14} />
                        ) : (
                            <FeatherIcon icon="check" color="#fff" size={14} />
                        )
                    }
                >
                    <Avatar src={image} size={28} />
                </Badge>}
            <EllipsisLabel
                style={{
                    maxWidth: "150px",
                    marginLeft: "4px",
                    color:
                        status === "no_show_up" ? "#AFAFAF" : "#000000",
                }}
            >
                {name ?? "Undefined"}
            </EllipsisLabel>
        </div>
    }

    const selectedPros = map(cart_products, (cp) => {
        return cp?.provider?.provider_id;
    })

    const columns = map(cart_products, (cp, i) => {
        const provider = cp?.provider || {};
        let tempPros = filter(offers_json, (pro) => {
            return !includes(selectedPros, pro?.provider_id) && !includes(["Offer Expired", "rejected", "cancelled", "error"], pro?.status);
        })
        if (isEmpty(provider)) {
            if (isEmpty(tempPros)) {
                return {
                    title: <div className={styles["light-cells"] + " centralize"}>Provider {i + 1}</div>,
                    dataIndex: `provider${i + 1}`,
                    key: `provider${i + 1}`,
                    align: "center",
                    width: providerColumnMinWidth,
                    onCell: (_, index) => ({
                        colSpan: index === (1 + cellToAdd) || index === (number_of_slots + 2 + cellToAdd) ? 0 : 1,
                    })
                }
            }
            return {
                title: <div className={styles["light-cells"]}>
                    <Select
                        placeholder={`Select Provider ${i + 1}`}
                        className={styles["pro-select"]}
                        onChange={(value) => {
                            assignProvider(value, cp?.cart_product_id);
                        }}>
                        {map(tempPros, (pro, it) => (<Select.Option
                            key={`pro-${it}`}
                            value={pro?.provider_id}
                            className={styles["providers-select"]}
                            style={{ padding: "2px" }}
                        >{getNameOfPro(pro)}</Select.Option>))}
                    </Select>
                </div>,
                dataIndex: `provider${i + 1}`,
                key: `provider${i + 1}`,
                align: "center",
                width: providerColumnMinWidth,
                onCell: (_, index) => ({
                    colSpan: index === (1 + cellToAdd) || index === (number_of_slots + 2 + cellToAdd) ? 0 : 1,
                })
            };
        } else {
            return {
                title: <div className={styles["light-cells"] + " centralize"}>{getNameOfPro(provider, true)}</div>,
                dataIndex: `provider${i + 1}`,
                key: `provider${i + 1}`,
                align: "center",
                width: providerColumnMinWidth,
                onCell: (_, index) => ({
                    colSpan: index === (1 + cellToAdd) || index === (number_of_slots + 2 + cellToAdd) ? 0 : 1,
                })
            }
        }

    })

    const generateDataSource = () => {
        let breaksTaken = [];
        let sessionsTaken = [];
        let breakSpanMap = new Map(); // Track break spans across time slots
        let sessionSpanMap = new Map(); // Track session spans across time slots
        
        let dataSource = compact([
            assign({
                key: 0,
                mainColumn: "Room Number",
            },
                ...map(cart_products, (cp, i) => ({
                    [`provider${i + 1}`]: (<div className={styles["light-cells"] + " centralize"}>
                        {get(cp, "room_number", "")}
                    </div>
                    ),
                })),
            ),
            break_minutes ? assign({
                key: 1,
                mainColumn: "Total Break",
            },
                ...(map(cart_products, (cp, i) => {
                    return {
                        [`provider${i + 1}`]: <div className={styles["light-cells"]}>
                            <span className="color-success centralize"><CheckCircle className="feather mr-8" size={18} />
                                <span className={"color-success"}> {break_minutes} of {break_minutes} mins</span>
                            </span>
                        </div>
                    }
                })),
            ) : null,
            {
                key: 2,
                mainColumn: <div className="rm-btb h-50 bg-grayish"></div>
            },
        ]);

        // Pre-process breaks to determine their span across time slots
        map(cart_products, (cp, providerIndex) => {
            const breaks = filter(get(cp, "slots", []), (sl) => sl.slot_type === "break");
            
            breaks.forEach((breakSlot) => {
                const breakStart = moment(`${session_date} ${breakSlot.start_time}`, "YYYY-MM-DD h:mm A");
                const breakEnd = moment(`${session_date} ${breakSlot.end_time}`, "YYYY-MM-DD h:mm A");
                
                // Find which time slots this break spans
                const spanInfo = {
                    startSlotIndex: -1,
                    endSlotIndex: -1,
                    totalSpan: 0,
                    breakData: breakSlot,
                    breakDuration: breakEnd.diff(breakStart, "minutes"),
                    overlappingSlots: [], // Track all overlapping slots with their overlap info
                    renderInSlot: -1 // Which slot should render the full break element
                };
                
                for (let i = 0; i < number_of_slots; i++) {
                    const slotStart = setOfTimes[i]?.start_time;
                    const slotEnd = slotStart?.clone().add(slot_length_minutes, "minutes");
                    
                    if (!slotStart || !slotEnd) continue;
                    
                    // Check if break overlaps with this time slot
                    const overlapStart = moment.max(breakStart, slotStart);
                    const overlapEnd = moment.min(breakEnd, slotEnd);
                    
                    if (overlapStart.isBefore(overlapEnd)) {
                        if (spanInfo.startSlotIndex === -1) {
                            spanInfo.startSlotIndex = i;
                            spanInfo.renderInSlot = i; // Render in first overlapping slot
                        }
                        spanInfo.endSlotIndex = i;
                        spanInfo.totalSpan++;
                        
                        // Calculate overlap details for this slot
                        spanInfo.overlappingSlots.push({
                            slotIndex: i,
                            overlapStart: overlapStart,
                            overlapEnd: overlapEnd,
                            overlapDuration: overlapEnd.diff(overlapStart, "minutes"),
                            isFirst: spanInfo.startSlotIndex === i,
                            isLast: false // Will be updated after loop
                        });
                    }
                }
                
                // Mark the last overlapping slot
                if (spanInfo.overlappingSlots.length > 0) {
                    spanInfo.overlappingSlots[spanInfo.overlappingSlots.length - 1].isLast = true;
                }
                
                if (spanInfo.startSlotIndex !== -1) {
                    const spanKey = `${providerIndex}-${breakSlot.id}`;
                    breakSpanMap.set(spanKey, spanInfo);
                }
            });
        });
        
        // Pre-process sessions (client booked) to determine their span across time slots
        map(cart_products, (cp, providerIndex) => {
            const sessions = filter(get(cp, "slots", []), (sl) => sl.slot_type === "session" && (sl.first_name || sl.last_name));
            
            sessions.forEach((sessionSlot) => {
                const sessStart = moment(`${session_date} ${sessionSlot.start_time}`, "YYYY-MM-DD h:mm A");
                const sessEnd = moment(`${session_date} ${sessionSlot.end_time}`, "YYYY-MM-DD h:mm A");
                
                const spanInfo = {
                    startSlotIndex: -1,
                    endSlotIndex: -1,
                    totalSpan: 0,
                    sessionData: sessionSlot,
                    sessionDuration: sessEnd.diff(sessStart, "minutes"),
                    overlappingSlots: [],
                    renderInSlot: -1
                };
                
                for (let i = 0; i < number_of_slots; i++) {
                    const slotStart = setOfTimes[i]?.start_time;
                    const slotEnd = slotStart?.clone().add(slot_length_minutes, "minutes");
                    if (!slotStart || !slotEnd) continue;
                    
                    const overlapStart = moment.max(sessStart, slotStart);
                    const overlapEnd = moment.min(sessEnd, slotEnd);
                    if (overlapStart.isBefore(overlapEnd)) {
                        if (spanInfo.startSlotIndex === -1) {
                            spanInfo.startSlotIndex = i;
                            spanInfo.renderInSlot = i;
                        }
                        spanInfo.endSlotIndex = i;
                        spanInfo.totalSpan++;
                        spanInfo.overlappingSlots.push({
                            slotIndex: i,
                            overlapStart,
                            overlapEnd,
                            overlapDuration: overlapEnd.diff(overlapStart, "minutes"),
                            isFirst: spanInfo.startSlotIndex === i,
                            isLast: false
                        });
                    }
                }
                if (spanInfo.overlappingSlots.length > 0) {
                    spanInfo.overlappingSlots[spanInfo.overlappingSlots.length - 1].isLast = true;
                }
                if (spanInfo.startSlotIndex !== -1) {
                    const spanKey = `${providerIndex}-${sessionSlot.id}`;
                    sessionSpanMap.set(spanKey, spanInfo);
                }
            });
        });
        // Generate time slot rows with improved break handling
        for (let i = 0; i < number_of_slots; i++) {
            dataSource.push(assign({
                key: i + 3,
                mainColumn: <span className="rm-btb" style={{ verticalAlign: "top" }}>{setOfTimes[i]?.displayTime}</span>,
            },
                ...map(cart_products, (cp, j) => {
                    // Break elements handling (can co-exist with sessions)
                    const elements = [];
                    const spanKeyPrefix = `${j}-`;

                    // Render break item or overlays
                    for (let [key, spanInfo] of breakSpanMap) {
                        if (key.startsWith(spanKeyPrefix) && !includes(breaksTaken, spanInfo.breakData.id)) {
                            const currentSlotOverlap = spanInfo.overlappingSlots.find(overlap => overlap.slotIndex === i);
                            if (!currentSlotOverlap) continue;
                            const shouldRenderFullBreak = i === spanInfo.renderInSlot;
                            if (shouldRenderFullBreak) {
                                breaksTaken.push(spanInfo.breakData.id);
                                const breakSlot = spanInfo.breakData;
                                const breakStart = moment(`${session_date} ${breakSlot.start_time}`, "YYYY-MM-DD h:mm A");
                                const firstSlotStart = setOfTimes[spanInfo.startSlotIndex]?.start_time;
                                const offsetMinutes = breakStart.diff(firstSlotStart, "minutes");
                                const offsetPixels = getExactTimeOffset(offsetMinutes);
                                elements.push(
                                    <div 
                                        key={`break-${i}-${j}`} 
                                        className={styles["break-element"]}
                                        style={{
                                            backgroundColor: "#E6F2ED",
                                            padding: "0px 4px",
                                            textAlign: "left",
                                            display: "flex",
                                            alignItems: "stretch",
                                            justifyContent: "stretch",
                                            gap: "8px",
                                            marginBottom: "1px",
                                            borderRadius: "4px",
                                            border: "1px solid #FFFFFF",
                                            height: `${Math.max(15, getExactTimeHeight(spanInfo.breakDuration) + (2 * (spanInfo.overlappingSlots.length - 1)))}px`,
                                            position: "absolute",
                                            top: `${offsetPixels}px`,
                                            left: "2px",
                                            right: "2px",
                                            zIndex: 10,
                                        }}
                                    >
                                        <div style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            justifyContent: "flex-start",
                                            alignItems: "center",
                                            alignContent: "center",
                                            gap: "4px",
                                            flex: "1",
                                            minWidth: "0",
                                            height: "100%",
                                            textAlign: "left"
                                        }}>
                                            <div style={{
                                                color: "#20C997",
                                                fontSize: "12px",
                                                lineHeight: "1.33em",
                                                fontWeight: "500",
                                                fontFamily: "Poppins, -apple-system, BlinkMacSystemFont, sans-serif",
                                                whiteSpace: "nowrap",
                                                flexShrink: 0
                                            }}>Break</div>
                                            <div style={{
                                                color: "#545454",
                                                fontSize: "12px",
                                                lineHeight: "1.67em",
                                                fontWeight: "400",
                                                fontFamily: "Poppins, -apple-system, BlinkMacSystemFont, sans-serif",
                                                whiteSpace: "nowrap",
                                                textOverflow: "ellipsis",
                                                minWidth: "0",
                                                flex: "1 1 auto"
                                            }}>
                                                {breakSlot.start_time} - {breakSlot.end_time}
                                            </div>
                                        </div>
                                        <div className={`table-actions ${styles["break-element__actions"]}`}>
                                            <DropdownBase
                                                style={{ margin: "auto", height: "20px" }}
                                                className="actions-dropdown"
                                                classNameC="actions-dropdown-content-pt-0"
                                                action={["click"]}
                                                getPopupContainer={getDsEditPopupContainer(styles)}
                                                content={
                                                    <Menu
                                                        items={[
                                                            {
                                                                label: (
                                                                    <Restricted
                                                                        both={true}
                                                                        to="remove_slot"
                                                                        resource={b2b_type === "employee_wellness" ? "employee_wellness" : "shift"}
                                                                        fallback={<></>}
                                                                    >
                                                                        <Link
                                                                            to="#"
                                                                            onClick={(e) => {
                                                                                e?.preventDefault();
                                                                                // Get all other breaks (excluding the current one being moved)
                                                                                const otherBreaks = filter(get(cp, "slots", []), (el) => 
                                                                                    el.slot_type === "break" && el.id !== breakSlot.id
                                                                                );
                                                                                setBreakObj({
                                                                                    ...breakSlot,
                                                                                    currentLength: spanInfo.breakDuration,
                                                                                    otherBreaks: otherBreaks, // Pass other breaks to the modal
                                                                                })
                                                                                setSlotOptions(filter(get(cp, "slots", []), (el) => (!el.is_break)))
                                                                                setShowEditBreakModal(true);
                                                                            }}
                                                                        >
                                                                            <FeatherIcon icon="move" size={18} />
                                                                            Move Break
                                                                        </Link>
                                                                    </Restricted>
                                                                ),
                                                                key: "1",
                                                            },
                                                        ]}
                                                    ></Menu>
                                                }
                                            >
                                                <span style={{ display: "inline-flex", lineHeight: 0 }}>
                                                    <FeatherIcon color="#20C997" icon="more-vertical" size={16} />
                                                </span>
                                            </DropdownBase>
                                        </div>
                                    </div>
                                );
                            } else {
                                const overlapHeight = getExactTimeHeight(currentSlotOverlap.overlapDuration);
                                const slotStart = setOfTimes[i]?.start_time;
                                const breakStart = currentSlotOverlap.overlapStart;
                                const offsetInSlot = breakStart.diff(slotStart, "minutes");
                                const offsetPixels = getExactTimeOffset(offsetInSlot);
                                elements.push(
                                    <div 
                                        key={`break-overlay-${i}-${j}`}
                                        style={{
                                            backgroundColor: "rgba(230, 242, 237, 0.6)",
                                            height: `${overlapHeight}px`,
                                            border: "1px dashed #20C997",
                                            borderLeft: "3px solid #20C997",
                                            borderRadius: "4px",
                                            position: "absolute",
                                            top: `${offsetPixels}px`,
                                            left: "4px",
                                            right: "4px",
                                            zIndex: 5,
                                            opacity: 0.7,
                                            pointerEvents: "none"
                                        }}
                                    />
                                );
                            }
                        }
                    }

                    // Render session item or overlays
                    for (let [key, spanInfo] of sessionSpanMap) {
                        if (key.startsWith(spanKeyPrefix) && !includes(sessionsTaken, spanInfo.sessionData.id)) {
                            const currentSlotOverlap = spanInfo.overlappingSlots.find(overlap => overlap.slotIndex === i);
                            if (!currentSlotOverlap) continue;
                            const shouldRenderFullSession = i === spanInfo.renderInSlot;
                            if (shouldRenderFullSession) {
                                sessionsTaken.push(spanInfo.sessionData.id);
                                const sl = spanInfo.sessionData;
                                const { start_time, end_time, first_name, last_name } = sl;
                                const sessStart = moment(`${session_date} ${sl.start_time}`, "YYYY-MM-DD h:mm A");
                                const firstSlotStart = setOfTimes[spanInfo.startSlotIndex]?.start_time;
                                const offsetMinutes = sessStart.diff(firstSlotStart, "minutes");
                                const offsetPixels = getExactTimeOffset(offsetMinutes);
                                const lastInitial = last_name ? `${last_name.charAt(0)}` : "";

                                const sessionHeight = Math.max(
                                    15,
                                    getExactTimeHeight(spanInfo.sessionDuration) + (2 * (spanInfo.overlappingSlots.length - 1))
                                );
                                const cancelMenu = (
                                    <Menu
                                        items={[buildCancelSlotMenuItem({
                                            slotId: sl.id,
                                            cartProductId: cp.cart_product_id,
                                            b2bType: b2b_type,
                                            onCancel: cancelSlot,
                                        })]}
                                    />
                                );
                                elements.push(
                                    <DropdownBase
                                        key={`session-${i}-${j}-${sl.id}`}
                                        style={{ display: "block", position: "absolute", top: `${offsetPixels}px`, left: "2px", right: "2px", zIndex: 20 }}
                                        className="actions-dropdown"
                                        classNameC="actions-dropdown-content-pt-0"
                                        action={["click"]}
                                        getPopupContainer={getDsEditPopupContainer(styles)}
                                        content={cancelMenu}
                                    >
                                        <div
                                            className={styles["session-element"]}
                                            style={{
                                                backgroundColor: "#F2F4FF",
                                                padding: "2px 4px",
                                                textAlign: "left",
                                                display: "flex",
                                                alignItems: "center",
                                                marginBottom: "2px",
                                                borderRadius: "4px",
                                                border: "none",
                                                height: `${sessionHeight}px`,
                                                width: "100%",
                                            }}
                                        >
                                            <div className={styles["session-element__content"]}>
                                                <div className={styles["session-element__name"]}>
                                                    {first_name} {lastInitial}
                                                </div>
                                                <div className={styles["session-element__time"]}>
                                                    {start_time} - {end_time}
                                                </div>
                                            </div>
                                            <div className={`table-actions ${styles["session-element__actions"]}`}>
                                                <FeatherIcon color="#ADB4D2" icon="more-vertical" size={18} />
                                            </div>
                                        </div>
                                    </DropdownBase>
                                );
                            } else {
                                const overlapHeight = getExactTimeHeight(currentSlotOverlap.overlapDuration);
                                const slotStart = setOfTimes[i]?.start_time;
                                const sessStart = currentSlotOverlap.overlapStart;
                                const offsetInSlot = sessStart.diff(slotStart, "minutes");
                                const offsetPixels = getExactTimeOffset(offsetInSlot);
                                elements.push(
                                    <div 
                                        key={`session-overlay-${i}-${j}-${spanInfo.sessionData.id}`}
                                        style={{
                                            backgroundColor: "rgba(242, 244, 255, 0.6)",
                                            height: `${overlapHeight}px`,
                                            border: "1px dashed #464A5B",
                                            borderLeft: "3px solid #464A5B",
                                            borderRadius: "4px",
                                            position: "absolute",
                                            top: `${offsetPixels}px`,
                                            left: "4px",
                                            right: "4px",
                                            zIndex: 15,
                                            opacity: 0.8,
                                            pointerEvents: "none"
                                        }}
                                    />
                                );
                                elements.push(
                                    <div
                                        key={`session-overlay-actions-${i}-${j}-${spanInfo.sessionData.id}`}
                                        className={styles["session-continuation-actions"]}
                                        style={{ top: `${offsetPixels + 2}px` }}
                                    >
                                        <SlotCancelDropdown
                                            slotId={spanInfo.sessionData.id}
                                            cartProductId={cp.cart_product_id}
                                            b2bType={b2b_type}
                                            onCancel={cancelSlot}
                                            styles={styles}
                                            iconSize={16}
                                        />
                                    </div>
                                );
                            }
                        }
                    }

                    return ({
                        [`provider${j + 1}`]: (
                            <div key={`cell-${i}-${j}`}>
                                <div 
                                    className={styles["light-cells"] + " " + styles["time-slot-cell"]} 
                                    style={{ 
                                        height: baseTimeSlotHeight + "px",
                                        position: "relative",
                                        padding: "4px",
                                        overflow: "visible"
                                    }}
                                >
                                    {elements}
                                </div>
                            </div>
                        )
                    });
                })
            ));
        }
        dataSource.push({
            key: number_of_slots + 3,
            mainColumn: <div className="rm-btb h-50 bg-grayish"></div>
        })
        return dataSource;
    }
    const tableView = <div className={`table-order ${styles["appointment-timeslot-table"]}`}>
        <Table
            dataSource={generateDataSource()}
            columns={[{
                dataIndex: "mainColumn", key: "mainColumn", align: "right",
                width: 120,
                fixed: providerCount >= 3 ? "left" : false,
                onCell: (_, index) => ({
                    colSpan: index === (1 + cellToAdd) || index === (number_of_slots + 2 + cellToAdd) ? cart_products?.length + 1 : 1,
                })
            }, ...columns]}
            pagination={false}
            size="small"
            bordered
            loading={isLoading}
            scroll={{ x: tableScrollX }}
        />
    </div>
    return (<>
        <div className={`${styles["ds-edit-root"]} ${isSchedulerExpanded ? styles["ds-edit-fullscreen"] : ""}`}>
            <div className={styles["ds-edit-toolbar"]}>
                <Button
                    size="small"
                    type="default"
                    onClick={() => setIsSchedulerExpanded((expanded) => !expanded)}
                >
                    {isSchedulerExpanded ? "Exit full screen" : "Expand scheduler"}
                </Button>
            </div>
            {tableView}
        </div>
        <BreakModalEdit breakObj={breakObj} showEditBreakModal={showEditBreakModal} setShowEditBreakModal={setShowEditBreakModal}
            slotOptions={slotOptions} refetch={refetch} refetchWaivableFees={refetchWaivableFees} setIsLoading={setIsLoading}
            location_id={location_id} session_date={session_date} apptRange={{
                start: start_time,
                end: end_time,
            }}
            break_minutes={break_minutes}
        />
    </>)
}

export default AppointmentDSEdit;